/**
 * processTransactions.js
 *
 * Converts normalized transactions into { nodes, links } objects
 * compatible with reactjs-interactive-graph's addNodes() API.
 *
 * Each node:  { id, color, size, label }
 * Each link:  { source, target, color, width, weight }
 */

export function processTransactions(txs, whiteNodeId, opts = {}) {
    if (!Array.isArray(txs)) {
        return { nodes: [], links: [] };
    }

    const { labelFormatter } = opts;

    // ── Accumulate node data ────────────────────────────────
    const nodeAcc = new Map();   // id → { sizeAcc, network }

    // ── Accumulate link data ────────────────────────────────
    const linkAcc = new Map();   // "a-b" → { source, target, count, sum }

    for (const tx of txs) {
        if (!tx?.from || !tx?.to) {
            continue;
        }

        const from = tx.from;
        const to   = tx.to;
        const val  = Number(tx.valueNorm ?? 0);

        // Nodes
        if (!nodeAcc.has(from)) {
            nodeAcc.set(from, { sizeAcc: 0, network: tx.network });
        }
        if (!nodeAcc.has(to)) {
            nodeAcc.set(to, { sizeAcc: 0, network: tx.network });
        }

        nodeAcc.get(from).sizeAcc += val;
        nodeAcc.get(to).sizeAcc   += val;

        // Links — directional key (A→B and B→A are separate edges)
        const key = `${from}→${to}`;

        if (!linkAcc.has(key)) {
            linkAcc.set(key, { source: from, target: to, count: 0, sum: 0 });
        }
        const l = linkAcc.get(key);
        l.count += 1;
        l.sum   += val;
    }

    // ── Build node objects ──────────────────────────────────
    const maxAcc = Math.max(1, ...Array.from(nodeAcc.values()).map(n => n.sizeAcc));

    const nodes = [];
    for (const [id, n] of nodeAcc) {
        const ratio = Math.log1p(n.sizeAcc) / Math.log1p(maxAcc);
        const size  = 4 + ratio * 14;
        const color = id === whiteNodeId ? '#ffffff' : colorByNetwork(n.network);
        const label = labelFormatter ? labelFormatter(id, n.network) : id;

        nodes.push({ id, color, size, label });
    }

    // ── Build link objects ──────────────────────────────────
    const maxCount = Math.max(1, ...Array.from(linkAcc.values()).map(l => l.count));

    const links = [];
    for (const l of linkAcc.values()) {
        links.push({
            source: l.source,
            target: l.target,
            color:  '#555555',
            width:  0.3 + (l.count / maxCount) * 2.2,
            weight: l.sum,
            directed: true,
        });
    }

    return { nodes, links };
}

function colorByNetwork(net) {
    switch (net) {
    case 'Ethereum': return '#22c55e';
    case 'Polygon':  return '#a855f7';
    case 'BSC':      return '#eab308';
    case 'TON Testnet':      return '#67e8f9';
    case 'TON':      return '#3b82f6';
    default:         return '#999999';
    }
}